<template>
    <slot :ref="setReference" style="cursor: pointer" :placement="placement" />

    <Transition name="scale-fade" :duration="animationDurationMs">
        <Teleport v-if="modelOpen" :to="to">
            <div
                :style="[
                    floatingStyles,
                    {
                        display: middlewareData.hide?.referenceHidden
                            ? 'none'
                            : 'block'
                    }
                ]"
                :id="id"
                class="popper-floating"
                ref="floating"
            >
                <div
                    class="transition-content"
                    :class="{ 'transition-content--full-width': props.fullWidth }"
                >
                    <slot name="content" :placement="placement" />
                </div>
            </div>
        </Teleport>
    </Transition>
</template>

<script setup>
import { computed, shallowRef, useTemplateRef } from 'vue';
import { usePopperCore } from './usePopperCore.js';
import { resolveDomElement } from '../../utils/resolveDomElement.js';

const modelOpen = defineModel({ default: false });

const props = defineProps({
    to: {
        type: String,
        default: null,
        required: false
    },
    offset: {
        type: [Number, Object],
        default: 0,
        required: false
    },
    flip: {
        type: Object,
        default: () => ({}),
        required: false
    },
    options: {
        type: Object,
        default: () => ({}),
        required: false
    },
    fullWidth: {
        type: Boolean,
        default: false,
        required: false
    },
    placement: {
        type: String,
        default: 'bottom-start',
        required: false
    },
    constrainToViewport: {
        type: Boolean,
        default: false,
        required: false
    },
    constrainBoundary: {
        type: [String, Object],
        default: null,
        required: false
    },
    constrainPadding: {
        type: [Number, Object],
        default: 12,
        required: false
    },
    constrainDirection: {
        type: String,
        default: 'both',
        required: false
    },
    avoidPopperElements: {
        type: Boolean,
        default: true,
        required: false
    },
    disableOutsideClose: {
        type: Boolean,
        default: false,
        required: false
    },
    outsideClickIgnore: {
        type: Array,
        default: () => [],
        required: false
    },
    zIndex: {
        type: Number,
        default: 8,
        required: false
    }
});

const animationDurationMs = 100;
const animationDuration = `${animationDurationMs}ms`;
const transitionContentWidth = computed(() =>
    props.fullWidth ? '100%' : 'max-content'
);

const $floating = useTemplateRef('floating');
const $reference = shallowRef(null);

const setReference = (ref) =>
    resolveDomElement(ref, {
        label: 'Popper reference',
        onResolved: (el) => {
            $reference.value = el;
        }
    });

const { id, floatingStyles, middlewareData, placement, to } = usePopperCore({
    open: modelOpen,
    referenceRef: $reference,
    floatingRef: $floating,
    to: computed(() => props.to),
    placement: computed(() => props.placement),
    offset: computed(() => props.offset),
    flip: computed(() => props.flip),
    options: computed(() => props.options),
    fullWidth: computed(() => props.fullWidth),
    constrainToViewport: computed(() => props.constrainToViewport),
    constrainBoundary: computed(() => props.constrainBoundary),
    constrainPadding: computed(() => props.constrainPadding),
    constrainDirection: computed(() => props.constrainDirection),
    disableOutsideClose: computed(() => props.disableOutsideClose),
    avoidPopperElements: computed(() => props.avoidPopperElements),
    outsideClickIgnore: computed(() => props.outsideClickIgnore)
});

const transformOriginMap = {
    top: 'center bottom',
    'top-start': 'left bottom',
    'top-end': 'right bottom',
    bottom: 'center top',
    'bottom-start': 'left top',
    'bottom-end': 'right top',
    left: 'right center',
    'left-start': 'right top',
    'left-end': 'right bottom',
    right: 'left center',
    'right-start': 'left top',
    'right-end': 'left bottom'
};

const transformOrigin = computed(() => {
    const value = placement.value || 'bottom-start';
    return transformOriginMap[value] || 'center center';
});
</script>

<style scoped lang="scss">
@import '../../assets/styles/_main.scss';

.popper-floating {
    z-index: v-bind(zIndex);
}

.transition-content {
    width: v-bind(transitionContentWidth);
}

.transition-content--full-width {
    width: 100%;
}

.scale-fade-enter-active .transition-content,
.scale-fade-leave-active .transition-content {
    @include transition-default(
        (opacity, transform),
        v-bind(animationDuration)
    );
    transform-origin: v-bind(transformOrigin);
}

.scale-fade-enter-from .transition-content,
.scale-fade-leave-to .transition-content {
    opacity: 0;
    transform: scale(0.96);
    transform-origin: v-bind(transformOrigin);
}

.scale-fade-enter-to .transition-content,
.scale-fade-leave-from .transition-content {
    opacity: 1;
    transform: scale(1);
    transform-origin: v-bind(transformOrigin);
}
</style>
